/**
 * Intro Teaser Studio — the chyron burned onto the intro segment.
 * — drag the bar on the canvas, resize width / height with handles
 * — text, font, case, alignment, colours, accent bar, flag tab, border
 * — when it shows (start + duration) and the footage look
 * — per-channel, account default (synced) and named templates
 *
 * Preview geometry is the same math as helper/teaser_backend.py.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FaUndo, FaRedo, FaAlignLeft, FaAlignCenter, FaAlignRight, FaImage, FaTimes,
} from 'react-icons/fa';
import { useAuthContext } from '../../hooks/UseAuthContext';
import {
  TEASER_FONTS,
  TEASER_PRESETS,
  buildTeaserPayload,
  normalizeTeaserConfig,
  parseTeaserPayload,
  teaserFontCss,
  teaserLayoutMetrics,
} from './teaserPresets';
import { TEMPLATE_TYPES } from './compositionTemplates';
import { hexToRgba, useElementSize } from './bannerScene';
import {
  ColorField, Section, Segmented, SelectField, Slider, TemplateBar, Toggle, useHistory,
} from './studioKit';

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

function contentFilter(cfg) {
  const f = [];
  if (cfg.contentEffect === 'grayscale' || cfg.colorMode === 'grayscale') f.push('grayscale(1)');
  else if (cfg.colorMode === 'sepia') f.push('sepia(0.85)');
  else if (cfg.contentEffect === 'desaturate') f.push(`saturate(${1 - (cfg.contentDesaturate ?? 0.65)})`);
  else if (cfg.contentEffect === 'contrast') f.push(`contrast(${cfg.contrast ?? 1.15})`);
  return f.join(' ') || undefined;
}

const SAMPLE_FOOTAGE = `
  radial-gradient(circle at 72% 32%, rgba(251, 191, 36, 0.85) 0 7%, transparent 7.5%),
  radial-gradient(ellipse at 30% 110%, #14532d 0 38%, transparent 38.5%),
  radial-gradient(ellipse at 80% 115%, #166534 0 34%, transparent 34.5%),
  linear-gradient(180deg, #38bdf8 0%, #7dd3fc 45%, #bae6fd 62%, #4d7c0f 62.5%, #365314 100%)`;

/** Footage + chyron at `dims` px. `refDims` = export size (for min sizes / shadow). */
export function TeaserGraphic({ config, dims, refDims, footage, children }) {
  const cfg = normalizeTeaserConfig(config);
  const m = teaserLayoutMetrics(cfg, { ...dims, ref: refDims });
  const k = refDims ? dims.h / refDims.h : 1;
  const text = cfg.textTransform === 'uppercase'
    ? String(cfg.chyronText || '').toUpperCase()
    : cfg.textTransform === 'lowercase' ? String(cfg.chyronText || '').toLowerCase() : String(cfg.chyronText || '');
  const stripTop = m.boxY + m.accentH;
  const textTop = stripTop + Math.max(2 * k, (m.stripH - m.textSize) / 2);
  const align = cfg.textAlign || 'left';
  const innerLeft = m.boxX + m.flagW + m.textPadX;
  const innerRight = m.boxX + m.boxW - m.textPadX;

  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', background: '#000' }}>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: footage ? `center / cover no-repeat url("${footage}")` : SAMPLE_FOOTAGE,
          filter: contentFilter(cfg),
          boxShadow: cfg.vignette || cfg.contentEffect === 'vignette' ? `inset 0 0 ${dims.w * 0.12}px rgba(0,0,0,0.75)` : undefined,
        }}
      />
      {m.accentH > 0 && (
        <div style={{ position: 'absolute', left: m.boxX, top: m.boxY, width: m.boxW, height: m.accentH, background: cfg.accentColor }} />
      )}
      <div style={{ position: 'absolute', left: m.boxX, top: stripTop, width: m.boxW, height: m.stripH, background: hexToRgba(cfg.stripColor, cfg.stripOpacity ?? 0.93) }} />
      {m.flagW > 0 && (
        <>
          <div style={{ position: 'absolute', left: m.boxX, top: stripTop, width: m.flagW, height: m.stripH, background: cfg.accentColor }} />
          <div style={{ position: 'absolute', left: m.boxX + m.flagW, top: stripTop, width: Math.max(1, 3 * k), height: m.stripH, background: 'rgba(255,255,255,0.8)' }} />
        </>
      )}
      <div
        style={{
          position: 'absolute',
          top: textTop,
          left: align === 'right' ? undefined : align === 'center' ? innerLeft - m.textPadX : innerLeft,
          right: align === 'right' ? dims.w - innerRight : undefined,
          width: align === 'center' ? m.boxW - m.flagW : undefined,
          textAlign: align,
          whiteSpace: 'pre',
          color: cfg.textColor,
          fontFamily: teaserFontCss(cfg.fontFamily),
          fontWeight: cfg.fontWeight || 'bold',
          fontSize: m.textSize,
          lineHeight: 1,
          textShadow: cfg.textShadow !== false ? `${4 * k}px ${4 * k}px 0 rgba(0,0,0,0.8)` : undefined,
        }}
      >
        {text || ' '}
      </div>
      {m.bottomH > 0 && (
        <div style={{ position: 'absolute', left: m.boxX, top: stripTop + m.stripH - m.bottomH, width: m.boxW, height: m.bottomH, background: 'rgba(255,255,255,0.67)' }} />
      )}
      {cfg.borderEnabled && (
        <div
          style={{
            position: 'absolute',
            left: m.boxX,
            top: m.boxY,
            width: m.boxW,
            height: m.widgetH,
            boxShadow: `inset 0 0 0 ${Math.max(1, (cfg.borderWidth || 2) * k)}px ${cfg.borderColor}`,
          }}
        />
      )}
      {children ? children(m) : null}
    </div>
  );
}

function TeaserThumb({ config, orientation }) {
  const [ref, size] = useElementSize();
  const refDims = orientation === 'portrait' ? { w: 1080, h: 1920 } : { w: 1920, h: 1080 };
  const ar = refDims.w / refDims.h;
  let w = size.w;
  let h = w / ar;
  if (size.h && h > size.h) { h = size.h; w = h * ar; }
  return (
    <div ref={ref} style={{ position: 'absolute', inset: 0 }}>
      {w > 0 && (
        <div style={{ position: 'absolute', left: '50%', top: '50%', width: w, height: h, transform: 'translate(-50%,-50%)' }}>
          <TeaserGraphic config={config} dims={{ w, h }} refDims={refDims} />
        </div>
      )}
    </div>
  );
}

/* ── Canvas with a draggable / resizable chyron ── */
function TeaserCanvas({ config, orientation, onChange, onCheckpoint, footage, showSafe }) {
  const refDims = orientation === 'portrait' ? { w: 1080, h: 1920 } : { w: 1920, h: 1080 };
  const [areaRef, area] = useElementSize();
  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const rafRef = useRef(0);
  const [live, setLive] = useState(null);
  const [guide, setGuide] = useState(null);
  const cfg = live || config;
  const free = cfg.layoutMode !== 'classic';

  const ar = refDims.w / refDims.h;
  let w = Math.max(0, area.w - 32);
  let h = w / ar;
  if (h > area.h - 32) { h = Math.max(0, area.h - 32); w = h * ar; }
  const dims = { w, h };

  const begin = (e, mode) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    try { canvasRef.current.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    onCheckpoint();
    const r = canvasRef.current.getBoundingClientRect();
    let start = { ...config };
    if (!free) {
      // dragging a full-width bar switches it to free position, starting where it is
      const m = teaserLayoutMetrics(config, { ...dims, ref: refDims });
      start = { ...start, layoutMode: 'free', xPct: 0, widthPct: 100, yPct: (m.boxY / dims.h) * 100 };
    }
    dragRef.current = { mode, x: e.clientX - r.left, y: e.clientY - r.top, cfg: start, pid: e.pointerId };
  };

  const move = (e) => {
    const d = dragRef.current;
    if (!d || d.pid !== e.pointerId) return;
    const r = canvasRef.current.getBoundingClientRect();
    const dxPct = ((e.clientX - r.left - d.x) / dims.w) * 100;
    const dyPct = ((e.clientY - r.top - d.y) / dims.h) * 100;
    const c = d.cfg;
    let next = c;
    let g = null;
    if (d.mode === 'move') {
      let x = clamp(c.xPct + dxPct, 0, 100 - c.widthPct);
      const y = clamp(c.yPct + dyPct, 0, 100);
      const centre = x + c.widthPct / 2;
      if (!e.altKey && Math.abs(centre - 50) < (6 / dims.w) * 100) { x = 50 - c.widthPct / 2; g = 50; }
      next = { ...c, xPct: x, yPct: y };
    } else if (d.mode === 'w') {
      const right = c.xPct + c.widthPct;
      const x = clamp(c.xPct + dxPct, 0, right - 10);
      next = { ...c, xPct: x, widthPct: right - x };
    } else if (d.mode === 'e') {
      next = { ...c, widthPct: clamp(c.widthPct + dxPct, 10, 100 - c.xPct) };
    } else if (d.mode === 's') {
      next = { ...c, chyronHeightPct: clamp((c.chyronHeightPct ?? 0.12) + dyPct / 100, 0.04, 0.3) };
    }
    d.last = next;
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => { setLive(next); setGuide(g); });
  };

  const end = (e) => {
    const d = dragRef.current;
    if (!d || d.pid !== e.pointerId) return;
    dragRef.current = null;
    cancelAnimationFrame(rafRef.current);
    try { canvasRef.current.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    if (d.last) onChange(d.last);
    setLive(null);
    setGuide(null);
  };

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  return (
    <div ref={areaRef} className="st-stage__area">
      {dims.w > 0 && (
        <div
          ref={canvasRef}
          className="st-canvas"
          style={{ width: dims.w, height: dims.h }}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
        >
          <TeaserGraphic config={cfg} dims={dims} refDims={refDims} footage={footage}>
            {(m) => (
              <div
                className={`st-sel ${m.widgetH < 48 ? 'is-thin-h' : ''}`}
                style={{ left: m.boxX, top: m.boxY, width: m.boxW, height: m.widgetH, cursor: 'move', borderStyle: free ? 'solid' : 'dashed' }}
                onPointerDown={(e) => begin(e, 'move')}
                title={free ? 'Drag to move · handles resize' : 'Drag to move it anywhere (switches to free position)'}
              >
                <span className="st-sel__tag">
                  {free ? `${Math.round(m.boxW * (refDims.w / dims.w))}×${Math.round(m.widgetH * (refDims.h / dims.h))}` : 'Full width · bottom'}
                </span>
                <span className="st-sel__h st-sel__h--w" onPointerDown={(e) => begin(e, 'w')} />
                <span className="st-sel__h st-sel__h--e" onPointerDown={(e) => begin(e, 'e')} />
                <span className="st-sel__h st-sel__h--s" onPointerDown={(e) => begin(e, 's')} />
              </div>
            )}
          </TeaserGraphic>
          {showSafe && <div className="st-safe" />}
          {guide != null && <div className="st-guide st-guide--v" style={{ left: (guide / 100) * dims.w }} />}
        </div>
      )}
    </div>
  );
}

const PLACEMENTS = [
  { id: 'bl', label: 'Bottom left', xPct: 4, yPct: 80, widthPct: 70 },
  { id: 'bc', label: 'Bottom centre', xPct: 10, yPct: 80, widthPct: 80 },
  { id: 'bw', label: 'Bottom wide', xPct: 0, yPct: 84, widthPct: 100 },
  { id: 'mid', label: 'Middle', xPct: 10, yPct: 44, widthPct: 80 },
  { id: 'top', label: 'Top', xPct: 0, yPct: 4, widthPct: 100 },
];

export default function IntroTeaserPanel({
  teaserConfig,
  setTeaserConfig,
  presetId,
  setPresetId,
  introTagActive,
  orientation,
  user: userProp,
  userToken: tokenProp,
}) {
  const { user: ctxUser } = useAuthContext() || {};
  const user = userProp || ctxUser;
  const userToken = tokenProp || user?.token;
  const orient = orientation === 'portrait' ? 'portrait' : 'landscape';
  const cfg = useMemo(() => normalizeTeaserConfig(teaserConfig), [teaserConfig]);
  const history = useHistory(cfg, setTeaserConfig);
  const [mobileTab, setMobileTab] = useState('inspect');
  const [footage, setFootage] = useState('');
  const [showSafe, setShowSafe] = useState(false);
  const fileRef = useRef(null);
  const rootRef = useRef(null);

  useEffect(() => () => { if (footage) URL.revokeObjectURL(footage); }, [footage]);

  const markCustom = useCallback(() => { if (presetId !== 'custom') setPresetId('custom'); }, [presetId, setPresetId]);

  const update = useCallback((patch, opts) => {
    history.set((prev) => ({ ...normalizeTeaserConfig(prev), ...(typeof patch === 'function' ? patch(prev) : patch) }), opts);
    markCustom();
  }, [history, markCustom]);

  const applyPreset = (p) => {
    history.set(normalizeTeaserConfig(p.config), { commit: true });
    setPresetId(p.id);
  };

  const applySaved = useCallback((saved) => {
    if (!saved?.config) return;
    history.set(normalizeTeaserConfig(saved.config), { commit: true });
    setPresetId(saved.presetId || 'custom');
  }, [history, setPresetId]);

  useEffect(() => {
    const onKey = (e) => {
      if (!rootRef.current?.isConnected) return;
      const tag = e.target?.tagName || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === 'z') { e.preventDefault(); if (e.shiftKey) history.redo(); else history.undo(); }
      if (mod && e.key.toLowerCase() === 'y') { e.preventDefault(); history.redo(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [history]);

  const free = cfg.layoutMode !== 'classic';

  return (
    <div className="studio studio--teaser" ref={rootRef} data-tab={mobileTab}>
      <div className="studio__bar">
        <div className="studio__title">
          <h3>Intro Teaser</h3>
          <span>Chyron on segments tagged “intro”</span>
        </div>
        <div className="studio__bar-group">
          <span className={`studio__status ${introTagActive ? 'is-active' : ''}`}>
            {introTagActive ? 'intro tag found' : 'no intro tag yet'}
          </span>
          <button type="button" className="st-icon-btn" onClick={history.undo} disabled={!history.canUndo} title="Undo (Ctrl+Z)" aria-label="Undo"><FaUndo /></button>
          <button type="button" className="st-icon-btn" onClick={history.redo} disabled={!history.canRedo} title="Redo (Ctrl+Shift+Z)" aria-label="Redo"><FaRedo /></button>
        </div>
        <TemplateBar
          type={TEMPLATE_TYPES.INTRO_TEASER}
          user={user}
          userToken={userToken}
          noun="teaser"
          buildPayload={() => buildTeaserPayload(presetId, cfg)}
          parsePayload={parseTeaserPayload}
          onApply={applySaved}
        />
      </div>

      <div className="studio__body">
        <aside className="studio__rail">
          <div className="studio__panel-head"><span>Presets</span></div>
          <div className="st-presets">
            {TEASER_PRESETS.filter((p) => p.id !== 'custom').map((p) => (
              <button key={p.id} type="button" className={`st-preset ${presetId === p.id ? 'is-selected' : ''}`} onClick={() => applyPreset(p)}>
                <span className="st-preset__thumb"><TeaserThumb config={p.config} orientation="landscape" /></span>
                <span className="st-preset__body"><strong>{p.label}</strong><span>{p.description}</span></span>
              </button>
            ))}
          </div>
        </aside>

        <section className="studio__stage">
          <div className="st-stage__toolbar">
            <button type="button" className="st-btn st-btn--sm" onClick={() => fileRef.current?.click()} title="Preview over your own footage (not saved)">
              <FaImage /> {footage ? 'Change footage' : 'Try with your footage'}
            </button>
            {footage && (
              <button type="button" className="st-icon-btn" onClick={() => setFootage('')} title="Back to sample footage" aria-label="Remove preview footage"><FaTimes /></button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) setFootage(URL.createObjectURL(f));
                e.target.value = '';
              }}
            />
            <span className="st-spacer" />
            <Toggle label="Safe area" checked={showSafe} onChange={setShowSafe} />
            <span className="st-stage__meta">{orient === 'portrait' ? '1080×1920' : '1920×1080'}</span>
          </div>
          <TeaserCanvas
            config={cfg}
            orientation={orient}
            onChange={(next) => update(next)}
            onCheckpoint={history.checkpoint}
            footage={footage}
            showSafe={showSafe}
          />
        </section>

        <div className="studio__tabs" role="tablist">
          <button type="button" role="tab" aria-selected={mobileTab === 'rail'} className={mobileTab === 'rail' ? 'is-active' : ''} onClick={() => setMobileTab('rail')}>Presets</button>
          <button type="button" role="tab" aria-selected={mobileTab === 'inspect'} className={mobileTab === 'inspect' ? 'is-active' : ''} onClick={() => setMobileTab('inspect')}>Edit</button>
        </div>

        <aside className="studio__inspector">
          <Section title="Text">
            <input
              className="st-input"
              value={cfg.chyronText || ''}
              onChange={(e) => update({ chyronText: e.target.value })}
              placeholder="COMING UP..."
              aria-label="Teaser text"
            />
            <SelectField
              label="Font"
              value={TEASER_FONTS.some((f) => f.value === cfg.fontFamily) ? cfg.fontFamily : 'Arial Black'}
              onChange={(v) => update({ fontFamily: v }, { commit: true })}
              options={TEASER_FONTS.map((f) => ({ value: f.value, label: f.value }))}
            />
            <div className="st-grid2">
              <Segmented
                label="Align"
                options={[
                  { value: 'left', label: 'Left', icon: <FaAlignLeft /> },
                  { value: 'center', label: 'Centre', icon: <FaAlignCenter /> },
                  { value: 'right', label: 'Right', icon: <FaAlignRight /> },
                ]}
                value={cfg.textAlign || 'left'}
                onChange={(v) => update({ textAlign: v }, { commit: true })}
              />
              <Segmented
                label="Case"
                options={[{ value: 'none', label: 'Aa', title: 'As typed' }, { value: 'uppercase', label: 'AA', title: 'All caps' }]}
                value={cfg.textTransform === 'uppercase' ? 'uppercase' : 'none'}
                onChange={(v) => update({ textTransform: v }, { commit: true })}
              />
            </div>
            <div className="st-grid2">
              <Slider label="Size" value={cfg.textSizePct} min={0.03} max={0.16} step={0.001} scale={100} unit="%" decimals={1} onChange={(v) => update({ textSizePct: v })} />
              <ColorField label="Text colour" value={cfg.textColor} onChange={(v) => update({ textColor: v })} />
            </div>
            <div className="st-toggles">
              <Toggle label="Bold" checked={cfg.fontWeight !== 'normal'} onChange={(v) => update({ fontWeight: v ? 'bold' : 'normal' }, { commit: true })} />
              <Toggle label="Shadow" checked={cfg.textShadow !== false} onChange={(v) => update({ textShadow: v }, { commit: true })} />
            </div>
          </Section>

          <Section title="Placement">
            <Segmented
              options={[{ value: 'free', label: 'Free position' }, { value: 'classic', label: 'Full-width bottom' }]}
              value={free ? 'free' : 'classic'}
              onChange={(v) => update({ layoutMode: v }, { commit: true })}
            />
            {free && (
              <>
                <div className="st-chips">
                  {PLACEMENTS.map((p) => (
                    <button key={p.id} type="button" className="st-btn st-btn--sm" onClick={() => update({ xPct: p.xPct, yPct: p.yPct, widthPct: p.widthPct }, { commit: true })}>
                      {p.label}
                    </button>
                  ))}
                </div>
                <div className="st-grid2">
                  <Slider label="X" value={cfg.xPct} min={0} max={100} step={0.5} unit="%" decimals={1} onChange={(v) => update({ xPct: Math.min(v, 100 - cfg.widthPct) })} />
                  <Slider label="Y" value={cfg.yPct} min={0} max={100} step={0.5} unit="%" decimals={1} onChange={(v) => update({ yPct: v })} />
                  <Slider label="Width" value={cfg.widthPct} min={10} max={100} step={0.5} unit="%" decimals={1} onChange={(v) => update({ widthPct: v, xPct: Math.min(cfg.xPct, 100 - v) })} />
                  <Slider label="Bar height" value={cfg.chyronHeightPct} min={0.04} max={0.3} step={0.005} scale={100} unit="%" decimals={1} onChange={(v) => update({ chyronHeightPct: v })} />
                </div>
              </>
            )}
            {!free && (
              <Slider label="Bar height" value={cfg.chyronHeightPct} min={0.04} max={0.3} step={0.005} scale={100} unit="%" decimals={1} onChange={(v) => update({ chyronHeightPct: v })} />
            )}
            <Slider label="Text padding" value={cfg.textPadXPct} min={0.005} max={0.08} step={0.001} scale={100} unit="%" decimals={1} onChange={(v) => update({ textPadXPct: v })} />
          </Section>

          <Section title="Bar style">
            <div className="st-grid2">
              <ColorField label="Bar colour" value={cfg.stripColor} onChange={(v) => update({ stripColor: v })} />
              <Slider label="Bar opacity" value={cfg.stripOpacity} min={0.1} max={1} step={0.01} scale={100} unit="%" decimals={0} onChange={(v) => update({ stripOpacity: v })} />
            </div>
            <ColorField label="Accent colour" value={cfg.accentColor} onChange={(v) => update({ accentColor: v })} />
            <div className="st-toggles">
              <Toggle label="Accent bar" checked={cfg.showAccentBar !== false} onChange={(v) => update({ showAccentBar: v }, { commit: true })} />
              <Toggle label="Flag tab" checked={cfg.showFlagTab !== false} onChange={(v) => update({ showFlagTab: v }, { commit: true })} />
              <Toggle label="Bottom line" checked={cfg.showBottomLine !== false} onChange={(v) => update({ showBottomLine: v }, { commit: true })} />
              <Toggle label="Border" checked={!!cfg.borderEnabled} onChange={(v) => update({ borderEnabled: v }, { commit: true })} />
            </div>
            {cfg.showAccentBar !== false && (
              <Slider label="Accent bar thickness" value={cfg.accentBarHeightPct} min={0.002} max={0.03} step={0.001} scale={1000} decimals={0} onChange={(v) => update({ accentBarHeightPct: v })} />
            )}
            {cfg.showFlagTab !== false && (
              <Slider label="Flag width" value={cfg.flagWidthPct} min={0.005} max={0.06} step={0.001} scale={100} unit="%" decimals={1} onChange={(v) => update({ flagWidthPct: v })} />
            )}
            {cfg.borderEnabled && (
              <div className="st-grid2">
                <ColorField label="Border colour" value={cfg.borderColor} onChange={(v) => update({ borderColor: v })} />
                <Slider label="Border width" value={cfg.borderWidth} min={1} max={12} step={1} unit="px" onChange={(v) => update({ borderWidth: v })} />
              </div>
            )}
          </Section>

          <Section title="Timing">
            <Slider label="Appears after" value={cfg.startSec ?? 0} min={0} max={15} step={0.1} unit="s" decimals={1} onChange={(v) => update({ startSec: v })} />
            <Toggle label="Stay until the segment ends" checked={!(cfg.durationSec > 0)} onChange={(v) => update({ durationSec: v ? 0 : 4 }, { commit: true })} />
            {cfg.durationSec > 0 && (
              <Slider label="Shows for" value={cfg.durationSec} min={0.5} max={30} step={0.1} unit="s" decimals={1} onChange={(v) => update({ durationSec: v })} />
            )}
            <p className="st-hint">
              {cfg.durationSec > 0
                ? `Visible from ${(cfg.startSec || 0).toFixed(1)}s to ${((cfg.startSec || 0) + cfg.durationSec).toFixed(1)}s of each intro segment.`
                : `Visible from ${(cfg.startSec || 0).toFixed(1)}s until the end of each intro segment.`}
            </p>
          </Section>

          <Section title="Footage look" defaultOpen={false}>
            <Segmented
              label="Colour"
              options={[{ value: 'color', label: 'Full colour' }, { value: 'sepia', label: 'Sepia' }, { value: 'grayscale', label: 'B&W' }]}
              value={cfg.colorMode || 'color'}
              onChange={(v) => update({ colorMode: v, ...(v !== 'color' && ['grayscale'].includes(cfg.contentEffect) ? { contentEffect: 'none' } : {}) }, { commit: true })}
            />
            <SelectField
              label="Effect"
              value={cfg.contentEffect || 'none'}
              onChange={(v) => update({ contentEffect: v }, { commit: true })}
              options={[
                { value: 'none', label: 'None' },
                { value: 'grayscale', label: 'Black & white' },
                { value: 'desaturate', label: 'Muted colours' },
                { value: 'contrast', label: 'High contrast' },
                { value: 'vignette', label: 'Vignette' },
              ]}
            />
            {cfg.contentEffect === 'desaturate' && (
              <Slider label="Mute amount" value={cfg.contentDesaturate ?? 0.65} min={0.1} max={1} step={0.05} scale={100} unit="%" decimals={0} onChange={(v) => update({ contentDesaturate: v })} />
            )}
            {cfg.contentEffect === 'contrast' && (
              <Slider label="Contrast" value={cfg.contrast ?? 1.15} min={1} max={1.6} step={0.01} decimals={2} onChange={(v) => update({ contrast: v })} />
            )}
            {cfg.contentEffect !== 'vignette' && (
              <Toggle label="Add vignette" checked={!!cfg.vignette} onChange={(v) => update({ vignette: v }, { commit: true })} />
            )}
            <p className="st-hint">Applied to the intro footage behind the teaser.</p>
          </Section>
        </aside>
      </div>
    </div>
  );
}
